<script>
import Chart from "vue-apexcharts";
import { format, parseISO } from "date-fns";
import Spinner from "@/components/ui/Spinner";
import patientTaskService from "@/services/patient-task";

export default {
  name: "TaskGraph",

  components: {
    Chart,
    Spinner,
  },

  props: {
    patientId: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
    showDialogModal: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      options: {
        noData: {
          text: "Cargando...",
        },
        chart: {
          animations: {
            enabled: true,
            easing: "easeinout",
            speed: 800,
            animateGradually: {
              enabled: true,
              delay: 150,
            },
            dynamicAnimation: {
              enabled: true,
              speed: 350,
            },
          },
          brush: {
            enabled: false,
          },
          toolbar: {
            show: true,
            offsetX: 0,
            offsetY: 0,
            tools: {
              download: true,
              selection: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: false,
            },
          },
          zoom: {
            enabled: false,
          },
        },
        stroke: {
          curve: "smooth",
        },
        xaxis: {
          categories: [],
        },
      },

      series: [
        {
          name: this.name,
          data: [],
        },
      ],
    };
  },

  created() {
    this.getTasks();
  },

  methods: {
    close() {
      this.$emit("close");
    },

    /**
     * @description UC-REG-081: armado robusto de series para vitales con N
     *              indicadores (presión arterial = sistólica + diastólica,
     *              laboratorio = colesterol + triglicéridos, etc.).
     *
     *              Bugs previos que este método resuelve:
     *
     *              1. Sobrescribía `this.options` entero perdiendo
     *                 chart.animations, stroke.curve, etc. → ApexCharts
     *                 dejaba de animar / renderizar bien series múltiples.
     *              2. findIndex por nombre exacto: si un task venía con el
     *                 nombre normalizado distinto (mayúsculas, espacios
     *                 extra), creaba series nuevas y desincronizaba el chart.
     *              3. Series con distintos largos de data: si un task tenía
     *                 menos answerValues que otro, ApexCharts veía arrays
     *                 desalineados y mostraba solo puntos sueltos.
     *              4. Sin manejo de value null/undefined: un punto null
     *                 rompía la línea.
     *
     *              Estrategia nueva:
     *
     *              - Pre-pase: relevar TODAS las series únicas (por nombre
     *                normalizado lowercase+trim) y mapear name original.
     *              - Iterar tasks ordenados y para cada uno emitir un valor
     *                por cada serie. Si el task no tiene answerValue para
     *                esa serie → push null (ApexCharts lo respeta y la
     *                línea continúa). Si el value es NaN → null.
     *              - Reemplazar solo xaxis.categories en options (merge,
     *                no overwrite).
     */
    getTasks() {
      return patientTaskService
        .get({
          patientID: this.patientId,
          taskType: "vitals",
          query: {
            sort: { completedAt: 1 },
            completedAt: { $exists: true },
          },
        })
        .then((res) => {
          // Filtro por nombre del vital (case-insensitive + trim).
          const targetName = (this.name || "").trim().toLowerCase();
          const tasks = res.tasks.filter(
            (vital) =>
              (vital.name || "").trim().toLowerCase() === targetName &&
              vital.completedAt
          );

          if (!tasks.length) {
            this.options = {
              ...this.options,
              xaxis: { categories: [] },
            };
            this.series = [{ name: this.name, data: [] }];
            return;
          }

          // 1er paso: identificar TODAS las series únicas + preservar
          // el nombre original (con tildes/mayúsculas) para mostrar.
          const seriesByKey = new Map();
          tasks.forEach((task) => {
            (task.answerValues || []).forEach((av) => {
              const key = (av.name || "").trim().toLowerCase();
              if (!key) return;
              if (!seriesByKey.has(key)) {
                seriesByKey.set(key, { name: av.name, data: [] });
              }
            });
          });

          // 2do paso: armar timestamps + completar cada serie con valor o
          // null si esa medición no estaba en ese task. Series quedan
          // sincronizadas en largo.
          const categories = [];
          tasks.forEach((task) => {
            categories.push(
              format(parseISO(task.completedAt), "dd/MM/yyyy-HH:mm")
            );
            const valuesByKey = new Map();
            (task.answerValues || []).forEach((av) => {
              const key = (av.name || "").trim().toLowerCase();
              const numeric =
                av.value === null || av.value === undefined
                  ? null
                  : Number(av.value);
              valuesByKey.set(key, Number.isFinite(numeric) ? numeric : null);
            });
            seriesByKey.forEach((serie, key) => {
              serie.data.push(
                valuesByKey.has(key) ? valuesByKey.get(key) : null
              );
            });
          });

          // Si el vital es 1-indicador y el answerValue tiene el mismo
          // nombre que el vital (caso peso, glucemia), mostrar solo "Valor"
          // como label de serie para no duplicar el título del chart.
          const seriesArray = Array.from(seriesByKey.values());
          if (
            seriesArray.length === 1 &&
            (seriesArray[0].name || "").trim().toLowerCase() === targetName
          ) {
            seriesArray[0].name = "Valor";
          }

          // Merge en options (NO sobreescribir el objeto entero — antes se
          // perdían animations, stroke, etc.).
          this.options = {
            ...this.options,
            xaxis: { categories },
          };
          this.series = seriesArray;
        });
    },
  },
};
</script>

<template lang="pug">
  el-dialog(name="modal-task-graph", :visible.sync="showDialogModal", :before-close="close")
    h2.sign__title {{ name | capitalize }}

    chart(width="100%", type="line", :options="options", :series="series")

    .el-dialog__footer
      button.button.button--blue(@click="close") Salir
</template>
